import React, { useEffect, useContext } from "react";
import PropTypes from "prop-types";
import TailwindWrapper from "../../components/TailwindWrapper/TailwindWrapper";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import MotionChevronRight from "../../shared/Icons/MotionChevronRight";
import { useTranslation } from "react-i18next";
import { ENV } from "../../utils/EmpUtil";
import {
  trackPageLoadEvent,
  trackInternalReferralExit,
  trackOnClickEvent,
} from "../../shared/services/analytics/AnalyticsService";
import { ModalContext } from "../../shared/components/EMPModal/ModalContext";
import ChecklistPDF from "../ChecklistPDF";
import PDF from "../../shared/content/pricing-job-aid-compressed.pdf";

const ConfigurePricing = ({ user }) => {
  const { t } = useTranslation();
  const { handleModal } = useContext(ModalContext);

  useEffect(() => {
    if (user) {
      trackPageLoadEvent("fv:emp:nvc:configure pricing", user, "emp:nvc");
    }
  }, [user]);

  const handleModalClick = () => {
    handleModal(
      t("NVCOnboarding.configurePricing.checklist"),
      <ChecklistPDF file={PDF} />
    );
  };

  /**
   * * Get the appropriate URL based on the current environment and URL type
   * @param {string} urlType - The type of URL mapping to use
   * @returns {string} The environment-specific URL
   */
  const getPartsUrl = (urlType) => {
    const urlMappings = {
      b2c: {
        dev: "https://dealer-partsfco-dev.ford.com",
        qa: "https://dealer-partsfco-qa.ford.com",
        edu: "https://dealer-partsfco-stage.ford.com",
        stg: "https://dealer-partsfco-stage.ford.com",
        prod: "https://dealer-partsfco.ford.com",
      },
    };

    const mapping = urlMappings[urlType];
    if (!mapping) {
      console.warn(`URL mapping for type "${urlType}" not found`);
      return null;
    }

    return mapping[ENV] || mapping.qa;
  };

  /**
   * Get the appropriate parts.ford.com URL based on the current environment
   * @returns {string} The environment-specific URL
   */
  const getB2CUrl = () => getPartsUrl("b2c");

  return (
    <TailwindWrapper>
      <div className="tw-mb-6">
        <h3 className="tw-text-3xl tw-font-normal tw-text-gray-900 tw-mb-6 tw-mt-0">
          {t("NVCOnboarding.configurePricing.title")}
        </h3>
        <div className="tw-mb-4">
          <EMPButton
            variant="outlined"
            rightIcon={<MotionChevronRight />}
            onClick={() => {
              // Track analytics click event
              if (user) {
                trackOnClickEvent(
                  "fv:emp:nvc:configure pricing",
                  "emp:nvc:configure pricing:content:pricing job aid",
                  "fv:emp:nvc:configure pricing:content action",
                  user,
                  "emp:nvc"
                );
              }
              handleModalClick();
            }}
          >
            {t("NVCOnboarding.configurePricing.checklist")}
          </EMPButton>
        </div>
        <p className="tw-text-md tw-mb-9">
          {t("NVCOnboarding.configurePricing.introDescription")}
        </p>

        <p className="tw-text-md tw-mb-6">
          {t("NVCOnboarding.configurePricing.introDescription2")}
        </p>

        <div className="tw-flex tw-items-center tw-gap-4">
          <div>
            <EMPButton
              variant="outlined"
              rightIcon={<MotionChevronRight />}
              href={getB2CUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (user) {
                  trackInternalReferralExit(
                    getB2CUrl(),
                    user,
                    "emp:nvc",
                    "fpol:home"
                  );
                }
              }}
            >
              {t("NVCOnboarding.configurePricing.b2cButtonText")}
            </EMPButton>
          </div>
        </div>
      </div>
    </TailwindWrapper>
  );
};

ConfigurePricing.propTypes = {
  user: PropTypes.object,
};

export default ConfigurePricing;
